import React, { useState } from 'react';
import { DollarSign, CreditCard, FileText, TrendingUp, Calendar, Download, X, CheckCircle2, AlertCircle, Edit2, Printer } from 'lucide-react';
import './BillingCard.css';

const BillingCard = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    paymentMethod: 'credit_card',
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
    notes: ''
  });
  const [adjustmentData, setAdjustmentData] = useState({
    type: 'discount',
    amount: '',
    reason: ''
  });

  const invoices = [
    {
      id: 1,
      invoiceNumber: 'Invoice #INV-001',
      patientService: 'John Smith - Annual Checkup',
      patientName: 'John Smith',
      dueDate: 'Dec 15, 2024',
      invoiceDate: 'Dec 1, 2024',
      amount: '350.00',
      status: 'Paid',
      lineItems: [
        { description: 'Annual Physical Examination', quantity: 1, rate: 250.00, amount: 250.00 },
        { description: 'Blood Pressure Check', quantity: 1, rate: 50.00, amount: 50.00 },
        { description: 'Lab Processing Fee', quantity: 1, rate: 50.00, amount: 50.00 }
      ],
      payments: [
        { date: 'Dec 15, 2024', method: 'Credit Card', amount: 350.00 }
      ]
    },
    {
      id: 2,
      invoiceNumber: 'Invoice #INV-002',
      patientService: 'Sarah Johnson - Consultation',
      patientName: 'Sarah Johnson',
      dueDate: 'Dec 20, 2024',
      invoiceDate: 'Dec 5, 2024',
      amount: '220.00',
      status: 'Pending',
      lineItems: [
        { description: 'Initial Consultation', quantity: 1, rate: 180.00, amount: 180.00 },
        { description: 'Administrative Fee', quantity: 1, rate: 40.00, amount: 40.00 }
      ],
      payments: []
    },
    {
      id: 3,
      invoiceNumber: 'Invoice #INV-003',
      patientService: 'Michael Davis - Physical Therapy',
      patientName: 'Michael Davis',
      dueDate: 'Nov 30, 2024',
      invoiceDate: 'Nov 15, 2024',
      amount: '180.00',
      status: 'Overdue',
      lineItems: [
        { description: 'Physical Therapy Session', quantity: 2, rate: 80.00, amount: 160.00 },
        { description: 'Equipment Usage', quantity: 1, rate: 20.00, amount: 20.00 }
      ],
      payments: []
    },
    {
      id: 4,
      invoiceNumber: 'Invoice #INV-004',
      patientService: 'Emma Wilson - Routine Screening',
      patientName: 'Emma Wilson',
      dueDate: 'Dec 25, 2024',
      invoiceDate: 'Dec 10, 2024',
      amount: '280.00',
      status: 'Paid',
      lineItems: [
        { description: 'Routine Health Screening', quantity: 1, rate: 200.00, amount: 200.00 },
        { description: 'Blood Work Analysis', quantity: 1, rate: 80.00, amount: 80.00 }
      ],
      payments: [
        { date: 'Dec 23, 2024', method: 'Debit Card', amount: 280.00 }
      ]
    },
    {
      id: 5,
      invoiceNumber: 'Invoice #INV-005',
      patientService: 'David Brown - Dental Cleaning',
      patientName: 'David Brown',
      dueDate: 'Dec 28, 2024',
      invoiceDate: 'Dec 12, 2024',
      amount: '150.00',
      status: 'Pending',
      lineItems: [
        { description: 'Dental Cleaning', quantity: 1, rate: 120.00, amount: 120.00 },
        { description: 'Fluoride Treatment', quantity: 1, rate: 30.00, amount: 30.00 }
      ],
      payments: []
    }
  ];

  const billingStats = [
    {
      title: 'Total Revenue',
      value: '$12,450',
      change: '+8.5%',
      icon: <DollarSign size={24} />,
      color: '#28a745'
    },
    {
      title: 'Outstanding',
      value: '$2,180',
      change: '-12%',
      icon: <CreditCard size={24} />,
      color: '#DAA520'
    },
    {
      title: 'This Month',
      value: '$4,230',
      change: '+15.2%',
      icon: <Calendar size={24} />,
      color: '#17a2b8'
    },
    {
      title: 'Pending Invoices',
      value: '8',
      change: '+2',
      icon: <FileText size={24} />,
      color: '#ffc107'
    }
  ];

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return '#28a745';
      case 'pending':
        return '#ffc107';
      case 'overdue':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  const handleDownloadInvoice = (invoice) => {
    console.log('Downloading invoice:', invoice.invoiceNumber);
    alert(`Downloading ${invoice.invoiceNumber}`);
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
  };

  const handleProcessPayment = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentData(prev => ({ ...prev, amount: invoice.amount }));
    setShowPaymentModal(true);
  };

  const handlePaymentMethodChange = () => {
    setShowPaymentModal(false);
    setShowPaymentMethodModal(true);
  };

  const handleConfirmPayment = () => {
    console.log('Processing payment:', {
      invoice: selectedInvoice,
      payment: paymentData
    });
    alert(`Payment of $${paymentData.amount} processed successfully!`);
    setShowPaymentModal(false);
    setShowPaymentMethodModal(false);
    setSelectedInvoice(null);
    setPaymentData({
      amount: '',
      paymentMethod: 'credit_card',
      cardNumber: '',
      cardName: '',
      expiryDate: '',
      cvv: '',
      notes: ''
    });
  };

  const handleAdjustment = (invoice) => {
    setSelectedInvoice(invoice);
    setShowAdjustmentModal(true);
  };

  const handleConfirmAdjustment = () => {
    console.log('Applying adjustment:', {
      invoice: selectedInvoice,
      adjustment: adjustmentData
    });
    alert(`Adjustment applied successfully!`);
    setShowAdjustmentModal(false);
    setSelectedInvoice(null);
    setAdjustmentData({
      type: 'discount',
      amount: '',
      reason: ''
    });
  };

  return (
    <div className="billing-container">
      <div className="billing-header">
        <div className="header-content">
          <h2>Billing & Invoices</h2>
          <p>Manage patient billing, invoices, and payment tracking</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-gold">
            <FileText size={16} /> New Invoice
          </button>
        </div>
      </div>

      {/* Billing Stats */}
      <div className="billing-stats">
        {billingStats.map((stat, index) => (
          <div key={index} className="stat-card">
            <div className="stat-icon" style={{ color: stat.color }}>
              {stat.icon}
            </div>
            <div className="stat-content">
              <h3>{stat.value}</h3>
              <p>{stat.title}</p>
              <span className="stat-change" style={{ color: stat.color }}>
                {stat.change}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Billing Content */}
      <div className="billing-content">
        {/* Recent Invoices */}
        <div className="invoices-section">
          <div className="section-header">
            <h3>Recent Invoices</h3>
            <div className="section-actions">
              <select 
                className="period-select"
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
              >
                <option value="week">This Week</option>
                <option value="month">This Month</option>
                <option value="quarter">This Quarter</option>
                <option value="year">This Year</option>
              </select>
              <button className="btn btn-outline">
                <Download size={16} /> Export
              </button>
            </div>
          </div>

          <div className="invoices-list">
            {invoices.map((invoice, index) => (
              <div key={index} className="invoice-item">
                <div className="invoice-info">
                  <div className="invoice-header">
                    <h4>{invoice.invoiceNumber}</h4>
                    <span 
                      className="invoice-status"
                      style={{ color: getStatusColor(invoice.status) }}
                    >
                      {invoice.status}
                    </span>
                  </div>
                  <p className="invoice-service">{invoice.patientService}</p>
                  <span className="invoice-date">Due: {invoice.dueDate}</span>
                </div>
                <div className="invoice-actions">
                  <div className="invoice-amount">
                    <span className="amount-value">${invoice.amount}</span>
                  </div>
                  <div className="action-buttons">
                    <button 
                      className="action-btn"
                      onClick={() => handleDownloadInvoice(invoice)}
                      title="Download"
                    >
                      <Download size={16} />
                    </button>
                    <button 
                      className="action-btn" 
                      onClick={() => handleViewInvoice(invoice)}
                      title="View Details"
                    >
                      <FileText size={16} />
                    </button>
                    {invoice.status !== 'Paid' && (
                      <button 
                        className="action-btn btn-pay" 
                        onClick={() => handleProcessPayment(invoice)}
                        title="Process Payment"
                      >
                        <CreditCard size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Payment Summary */}
        <div className="payment-summary">
          <div className="section-header">
            <h3>Payment Summary</h3>
          </div>
          <div className="summary-grid">
            <div className="summary-card">
              <div className="summary-icon">
                <TrendingUp size={24} />
              </div>
              <div className="summary-content">
                <h4>Total Revenue</h4>
                <p className="summary-value">$12,450</p>
                <span className="summary-change positive">+8.5% from last month</span>
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-icon">
                <CreditCard size={24} />
              </div>
              <div className="summary-content">
                <h4>Outstanding</h4>
                <p className="summary-value">$2,180</p>
                <span className="summary-change negative">-12% from last month</span>
              </div>
            </div>
            <div className="summary-card">
              <div className="summary-icon">
                <Calendar size={24} />
              </div>
              <div className="summary-content">
                <h4>This Month</h4>
                <p className="summary-value">$4,230</p>
                <span className="summary-change positive">+15.2% from last month</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Details Modal */}
      {showInvoiceModal && selectedInvoice && (
        <div className="billing-modal-overlay" onClick={() => setShowInvoiceModal(false)}>
          <div className="billing-modal billing-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="billing-modal-header">
              <h2>Invoice Details</h2>
              <button className="billing-modal-close" onClick={() => setShowInvoiceModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="billing-modal-body">
              <div className="invoice-detail-header">
                <div className="invoice-detail-info">
                  <h3>{selectedInvoice.invoiceNumber}</h3>
                  <p>Patient: <strong>{selectedInvoice.patientName}</strong></p>
                  <p>Invoice Date: {selectedInvoice.invoiceDate}</p>
                  <p>Due Date: {selectedInvoice.dueDate}</p>
                </div>
                <div className="invoice-detail-status">
                  <span 
                    className={`status-badge status-${selectedInvoice.status.toLowerCase()}`}
                    style={{ color: getStatusColor(selectedInvoice.status) }}
                  >
                    {selectedInvoice.status}
                  </span>
                  <div className="invoice-total">
                    <span className="total-label">Total Amount:</span>
                    <span className="total-value">${selectedInvoice.amount}</span>
                  </div>
                </div>
              </div>
              
              <div className="invoice-line-items">
                <h4>Line Items</h4>
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Qty</th>
                      <th>Rate</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInvoice.lineItems.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.description}</td>
                        <td>{item.quantity}</td>
                        <td>${item.rate.toFixed(2)}</td>
                        <td>${item.amount.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="3"><strong>Total</strong></td>
                      <td><strong>${selectedInvoice.amount}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {selectedInvoice.payments.length > 0 && (
                <div className="invoice-payments">
                  <h4>Payment History</h4>
                  {selectedInvoice.payments.map((payment, idx) => (
                    <div key={idx} className="payment-record">
                      <CheckCircle2 size={18} color="#28a745" />
                      <span>{payment.date}</span>
                      <span>{payment.method}</span>
                      <span className="payment-amount">${payment.amount.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="billing-modal-footer">
              <button className="btn btn-outline" onClick={() => handleDownloadInvoice(selectedInvoice)}>
                <Download size={16} /> Download PDF
              </button>
              <button className="btn btn-outline">
                <Printer size={16} /> Print
              </button>
              {selectedInvoice.status !== 'Paid' && (
                <>
                  <button className="btn btn-outline" onClick={() => handleAdjustment(selectedInvoice)}>
                    <Edit2 size={16} /> Adjustment
                  </button>
                  <button className="btn btn-gold" onClick={() => handleProcessPayment(selectedInvoice)}>
                    <CreditCard size={16} /> Process Payment
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedInvoice && (
        <div className="billing-modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="billing-modal" onClick={(e) => e.stopPropagation()}>
            <div className="billing-modal-header">
              <h2>Process Payment</h2>
              <button className="billing-modal-close" onClick={() => setShowPaymentModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="billing-modal-body">
              <div className="payment-summary">
                <div className="summary-item">
                  <span>Invoice:</span>
                  <strong>{selectedInvoice.invoiceNumber}</strong>
                </div>
                <div className="summary-item">
                  <span>Patient:</span>
                  <strong>{selectedInvoice.patientName}</strong>
                </div>
                <div className="summary-item">
                  <span>Amount Due:</span>
                  <strong className="amount-highlight">${selectedInvoice.amount}</strong>
                </div>
              </div>

              <div className="form-group">
                <label>Payment Amount</label>
                <input 
                  type="number"
                  placeholder="Enter amount"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
                />
              </div>

              <div className="form-group">
                <label>Payment Method</label>
                <select 
                  value={paymentData.paymentMethod}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, paymentMethod: e.target.value }))}
                >
                  <option value="credit_card">Credit Card</option>
                  <option value="debit_card">Debit Card</option>
                  <option value="cash">Cash</option>
                  <option value="check">Check</option>
                  <option value="insurance">Insurance</option>
                </select>
              </div>

              <div className="form-group">
                <label>Payment Notes (Optional)</label>
                <textarea 
                  rows="3"
                  placeholder="Add any notes about this payment..."
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, notes: e.target.value }))}
                />
              </div>
            </div>
            <div className="billing-modal-footer">
              <button className="btn btn-outline" onClick={() => setShowPaymentModal(false)}>Cancel</button>
              <button className="btn btn-outline" onClick={handlePaymentMethodChange}>
                <CreditCard size={16} /> Enter Card Details
              </button>
              <button className="btn btn-gold" onClick={handleConfirmPayment}>
                Process ${paymentData.amount || '0.00'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Method Modal (Card Details) */}
      {showPaymentMethodModal && selectedInvoice && (
        <div className="billing-modal-overlay" onClick={() => setShowPaymentMethodModal(false)}>
          <div className="billing-modal" onClick={(e) => e.stopPropagation()}>
            <div className="billing-modal-header">
              <h2>Enter Card Details</h2>
              <button className="billing-modal-close" onClick={() => setShowPaymentMethodModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="billing-modal-body">
              <div className="payment-info-banner">
                <AlertCircle size={20} color="#1f73e6" />
                <p>Your payment information is encrypted and secure</p>
              </div>

              <div className="form-group">
                <label>Cardholder Name</label>
                <input 
                  type="text"
                  placeholder="John Doe"
                  value={paymentData.cardName}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, cardName: e.target.value }))}
                />
              </div>

              <div className="form-group">
                <label>Card Number</label>
                <input 
                  type="text"
                  placeholder="1234 5678 9012 3456"
                  maxLength="19"
                  value={paymentData.cardNumber}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, cardNumber: e.target.value }))}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Expiry Date</label>
                  <input 
                    type="text"
                    placeholder="MM/YY"
                    maxLength="5"
                    value={paymentData.expiryDate}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, expiryDate: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>CVV</label>
                  <input 
                    type="text"
                    placeholder="123"
                    maxLength="4"
                    value={paymentData.cvv}
                    onChange={(e) => setPaymentData(prev => ({ ...prev, cvv: e.target.value }))}
                  />
                </div>
              </div>

              <div className="payment-summary">
                <div className="summary-item">
                  <span>Amount to Charge:</span>
                  <strong className="amount-highlight">${paymentData.amount}</strong>
                </div>
              </div>
            </div>
            <div className="billing-modal-footer">
              <button className="btn btn-outline" onClick={() => setShowPaymentMethodModal(false)}>Back</button>
              <button className="btn btn-gold" onClick={handleConfirmPayment}>
                <CheckCircle2 size={16} /> Confirm Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Adjustment Modal */}
      {showAdjustmentModal && selectedInvoice && (
        <div className="billing-modal-overlay" onClick={() => setShowAdjustmentModal(false)}>
          <div className="billing-modal" onClick={(e) => e.stopPropagation()}>
            <div className="billing-modal-header">
              <h2>Apply Adjustment</h2>
              <button className="billing-modal-close" onClick={() => setShowAdjustmentModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="billing-modal-body">
              <div className="adjustment-info">
                <p><strong>Invoice:</strong> {selectedInvoice.invoiceNumber}</p>
                <p><strong>Current Amount:</strong> ${selectedInvoice.amount}</p>
              </div>

              <div className="form-group">
                <label>Adjustment Type</label>
                <select 
                  value={adjustmentData.type}
                  onChange={(e) => setAdjustmentData(prev => ({ ...prev, type: e.target.value }))}
                >
                  <option value="discount">Discount</option>
                  <option value="refund">Refund</option>
                  <option value="writeoff">Write-off</option>
                  <option value="adjustment">Other Adjustment</option>
                </select>
              </div>

              <div className="form-group">
                <label>Adjustment Amount</label>
                <input 
                  type="number"
                  placeholder="Enter amount"
                  value={adjustmentData.amount}
                  onChange={(e) => setAdjustmentData(prev => ({ ...prev, amount: e.target.value }))}
                />
              </div>

              <div className="form-group">
                <label>Reason for Adjustment</label>
                <textarea 
                  rows="4"
                  placeholder="Explain the reason for this adjustment..."
                  value={adjustmentData.reason}
                  onChange={(e) => setAdjustmentData(prev => ({ ...prev, reason: e.target.value }))}
                  required
                />
              </div>

              {adjustmentData.amount && (
                <div className="adjustment-preview">
                  <div className="preview-item">
                    <span>Original Amount:</span>
                    <span>${selectedInvoice.amount}</span>
                  </div>
                  <div className="preview-item">
                    <span>Adjustment:</span>
                    <span className="adjustment-value">-${adjustmentData.amount}</span>
                  </div>
                  <div className="preview-item total">
                    <span><strong>New Amount:</strong></span>
                    <span><strong>${(parseFloat(selectedInvoice.amount) - parseFloat(adjustmentData.amount || 0)).toFixed(2)}</strong></span>
                  </div>
                </div>
              )}
            </div>
            <div className="billing-modal-footer">
              <button className="btn btn-outline" onClick={() => setShowAdjustmentModal(false)}>Cancel</button>
              <button 
                className="btn btn-gold" 
                onClick={handleConfirmAdjustment}
                disabled={!adjustmentData.amount || !adjustmentData.reason}
              >
                Apply Adjustment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingCard;
